import { Auth0Provider } from "@auth0/auth0-react";
// eslint-disable-next-line import/no-unassigned-import
import { TooltipProvider } from "@hortis/ui/tooltip";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { installTwicPics } from "@twicpics/components/react";
import "./styles/global.css";
// eslint-disable-next-line import/no-unassigned-import
import "@twicpics/components/style.css";
import { HelmetProvider } from "react-helmet-async";
import { MapProvider } from "react-map-gl";
import { PersistUserDomain } from "src/features/organisation/persist-user-domain";
import { InlineNoScriptWithConfig } from "./components/inline-js";
import { NewVersionPrompt } from "./components/new-version-prompt";
import config from "./config";
import { EnvironmentProvider } from "./environment";
import { UrqlProvider } from "./gql/urql-provider";
import { PostHogProvider } from "./helpers/posthog-provider";
import { router } from "./router";
import theme from "./theme";

export const queryClient = new QueryClient();

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const gtmInlineNoScript = (gtmApiKey: string) =>
  `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmApiKey}" height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`;

const cssBaseline = <CssBaseline />;

const auth0CacheLocation = "localstorage";

installTwicPics({
  domain: "https://ipp4fe79.twic.pics",
});

export const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={800}>
          <InlineNoScriptWithConfig inject={gtmInlineNoScript} />
          <EnvironmentProvider>
            <Auth0Provider
              domain={config.publicAuthDomain}
              clientId={config.publicAuthClientId}
              authorizationParams={{
                redirect_uri: window.location.origin,
                audience: config.publicAuthAudience,
              }}
              cacheLocation={auth0CacheLocation}
              useRefreshTokens
              useRefreshTokensFallback // See: https://community.auth0.com/t/auth0-spa-2-x-returning-missing-refresh-token/98999/29
            >
              <PostHogProvider>
                <PersistUserDomain>
                  <UrqlProvider>
                    <MapProvider>
                      <ThemeProvider theme={theme}>
                        {cssBaseline}
                        <RouterProvider router={router} />
                        <NewVersionPrompt />
                      </ThemeProvider>
                    </MapProvider>
                  </UrqlProvider>
                </PersistUserDomain>
              </PostHogProvider>
            </Auth0Provider>
          </EnvironmentProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};
